import type { ReactElement } from 'react';
import type { ExportFormatStructureNode } from '@capture/shared';
import styles from '../export-composer.module.css';
import { StructureTreeView } from '../../../export-formats/components/StructureTreeView';

/**
 * Slice 4b — read-only view of a BOUND format's structure, shown in the Form
 * Config → Export Structure tab when a named format is selected. Structure is
 * owned by the format (edited under Export Formats), so this view is purely
 * presentational: a recursive read-only tree (or a flat column list for legacy
 * formats with no structure). Deliberately NOT the editable FormatStructureNode
 * — mirrors the Slice-3b StructurePreview decision.
 */

export function BoundStructureView({
  formatName,
  structure,
  columns,
}: {
  formatName: string;
  structure?: ExportFormatStructureNode[] | null;
  columns: { name: string; dataType?: string }[];
}): ReactElement {
  const hasTree = !!structure && structure.length > 0;

  return (
    <div className={styles.roStructure}>
      <p className={styles.roNote}>
        Structure is defined by the bound format <strong>{formatName}</strong>. Edit it under
        Export Formats.
      </p>

      {hasTree ? (
        <div className={styles.roTree}>
          <StructureTreeView
            nodes={structure!}
            classes={{ node: styles.roNode, name: styles.roName, badge: styles.roBadge, required: styles.roRequired }}
            showRequired
            showArrayBadge
          />
        </div>
      ) : (
        <div className={styles.roTree}>
          {columns.map((c, i) => (
            <div className={styles.roNode} key={`${c.name}-${i}`}>
              <span className={styles.roName}>{c.name}</span>
              {c.dataType && <span className={styles.roBadge}>{c.dataType}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
